import React, { useState, useEffect } from 'react';
import { Eye, Download, Search, Filter } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { FeatureRequestDetailsModal } from './FeatureRequestDetailsModal';

interface FeatureRequest {
  id: string;
  user_role: string;
  feedback: string;
  screenshots: string[];
  examples: string[];
  created_at: string;
  user_id: string;
}

export function AdminFeatureRequests() {
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [selectedRequest, setSelectedRequest] = useState<FeatureRequest | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [emails, setEmails] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    async function fetchEmails() {
      const userIds = requests
        .filter(request => request.user_id)
        .map(request => request.user_id);

      if (userIds.length > 0) {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', userIds);

        if (!error && data) {
          const emailMap = data.reduce((acc, profile) => ({
            ...acc,
            [profile.id]: profile.email
          }), {});
          setEmails(emailMap);
        }
      }
    }

    if (requests.length > 0) {
      fetchEmails();
    }
  }, [requests]);

  async function fetchRequests() {
    try {
      const { data, error } = await supabase
        .from('feature_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  }

  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.feedback.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (emails[request.user_id] || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole === 'all' || request.user_role === selectedRole;
    return matchesSearch && matchesRole;
  });

  const exportToCSV = () => {
    const headers = ['Date', 'User Email', 'Role', 'Feedback', 'Screenshots', 'Examples'];
    const csvContent = [
      headers.join(','),
      ...filteredRequests.map(request => [
        new Date(request.created_at).toLocaleDateString(),
        request.user_id ? (emails[request.user_id] || 'Unknown') : 'Anonymous User',
        request.user_role,
        `"${request.feedback.replace(/"/g, '""')}"`,
        `"${request.screenshots?.join(', ') || ''}"`,
        `"${request.examples?.join(', ') || ''}"`,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'feature_requests.csv';
    link.click();
  };

  const handleViewDetails = (request: FeatureRequest) => {
    setSelectedRequest(request);
    setIsDetailsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5e9bb8]"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Feature Requests
        </h1>
        
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search requests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-[#5e9bb8] focus:border-[#5e9bb8] dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Filter className="text-gray-400 h-5 w-5" />
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-[#5e9bb8] focus:border-[#5e9bb8] dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="employee">Employee</option>
              <option value="customer">Customer</option>
            </select>
          </div>

          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2 bg-[#5e9bb8] text-white rounded-lg hover:bg-[#4d8299] transition-colors duration-200"
          >
            <Download className="h-5 w-5 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  User Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Feedback
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRequests.map((request) => (
                <tr 
                  key={request.id} 
                  onClick={() => handleViewDetails(request)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-all duration-200 transform hover:-translate-y-px hover:shadow-sm"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(request.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {request.user_id ? (emails[request.user_id] || 'Unknown') : 'Anonymous User'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      request.user_role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                      request.user_role === 'employee' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                      {request.user_role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-white line-clamp-2">
                      {request.feedback}
                    </div>
                    {(request.screenshots?.length > 0 || request.examples?.length > 0) && (
                      <div className="mt-1 flex gap-2">
                        {request.screenshots?.length > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                            {request.screenshots.length} Screenshot{request.screenshots.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {request.examples?.length > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                            {request.examples.length} Link{request.examples.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(request);
                      }}
                      className="text-[#5e9bb8] hover:text-[#4d8299] dark:text-[#5e9bb8] dark:hover:text-[#4d8299]"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedRequest && (
        <FeatureRequestDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          request={selectedRequest}
        />
      )}
    </div>
  );
}